/**
 * Website webhook notifications.
 *
 * Previously the admin POSTed directly to the consumer site's webhook URL
 * from the browser, with the URL + secret loaded into module state from
 * the account settings. That setup was unreliable — module state could
 * silently desync from the DB, the secret had to live in the bundle, and
 * every consumer needed CORS. Now we just call our own LittleBooker API
 * server-side endpoint, which reads the up-to-date webhook config from
 * the DB and dispatches the call from the server. The admin no longer
 * needs to know the consumer URL or the shared secret.
 */

import { ensureFreshAccessToken } from '@/contexts/auth/accessToken';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';


export type WebhookEventType =
  | 'shows-updated'
  | 'artists-updated'
  | 'site-settings-updated';

/**
 * Fire-and-forget notify call. Errors logged to console but never thrown —
 * the upstream consumer being temporarily unreachable should not block
 * an admin save.
 */
export async function notifyWebsite(
  eventType: WebhookEventType,
  additionalData?: Record<string, any>
): Promise<void> {
  const token = await ensureFreshAccessToken();
  if (!token) {
    // Not authenticated — nothing to notify with. Save itself will have
    // failed at the DAL layer if this happens during a real session.
    return;
  }

  try {
    fetch(`${API_BASE_URL}/api/v1/webhooks/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ type: eventType, ...additionalData }),
    })
      .then(async (response) => {
        if (response.ok) {
          console.log(`[Webhook] notified: ${eventType}`);
        } else {
          const text = await response.text().catch(() => '');
          console.warn(`[Webhook] notify returned ${response.status}`, text);
        }
      })
      .catch((error) => {
        console.error('[Webhook] notify failed:', error);
      });
  } catch (error) {
    console.error('[Webhook] notify threw synchronously:', error);
  }
}

/**
 * Awaited variant for the AccountSettingsPage "Send test ping" button.
 * Surfaces the result so the user gets immediate feedback.
 */
export async function testWebhook(): Promise<{ ok: boolean; status?: number; error?: string }> {
  const token = await ensureFreshAccessToken();
  if (!token) return { ok: false, error: 'Not authenticated' };
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/webhooks/notify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ type: 'site-settings-updated', test: true }),
    });
    return { ok: response.ok, status: response.status };
  } catch (error) {
    return { ok: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

export function notifyShowsUpdated(showSlug?: string): void {
  notifyWebsite('shows-updated', showSlug ? { showSlug } : undefined);
}

export function notifyArtistsUpdated(artistSlug?: string): void {
  notifyWebsite('artists-updated', artistSlug ? { artistSlug } : undefined);
}
